import React, { useId, useState, useRef, useEffect } from 'react';

import {
  DEFAULT_EMAIL_WIDGET_BUTTON_TEXT,
  SharedProps,
  WidgetProps,
  DEFAULT_EMAIL_LABEL
} from '../shared';

import { CFColorTokenProp } from '~/theme/tokens/contentful-color';
import { MobilePhoneNumberInput } from '../MobilePhoneNumberWidget/MobilePhoneNumberInput';
import { MobilePhoneNumberButton } from '../MobilePhoneNumberWidget/MobilePhoneNumberButton';
import { FloatLabel } from '~/components/Input/FloatLabel';

export function EmailWidget({
  buttonColor = 'ginger',
  buttonHoverColor = 'green',
  buttonText = DEFAULT_EMAIL_WIDGET_BUTTON_TEXT,
  label = DEFAULT_EMAIL_LABEL,
  errorMessage,
  errorPosition,
  showErrorBanner,
  submitted,
  inputProps,
  successMessage = 'Thank you!',
  isReturning,
  borderColor,
  responsive
}: WidgetProps & SharedProps) {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const inputId = useId();
  const errorId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const showSuccessMessage = !submitted;

  const { id, value } = inputProps ?? {};

  // Focus management: Move focus to input when error appears
  useEffect(() => {
    if (errorMessage) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 200);
    }
  }, [errorMessage]);

  return (
    <>
      <div
        className={
          'relative z-[3] flex h-full max-h-16 flex-row items-center justify-center'
        }
        // Keep non-interactive; focus via label click or direct input
        onMouseDownCapture={(e) => {
          // only redirect if clicking non-focusable areas (not inputs/buttons)
          const target = e.target as HTMLElement;
          if (
            target.tagName !== 'INPUT' &&
            target.tagName !== 'BUTTON' &&
            target.tagName !== 'LABEL'
          ) {
            e.preventDefault();
            inputRef.current?.focus();
            setIsFocused(true);
          }
        }}
        // Remove wrapper interactivity to avoid stealing focus; label handles keyboard focus
      >
        <div className="z-[1] flex h-full w-full items-center">
          <FloatLabel
            htmlFor={id ?? inputId}
            floatLabel={!!value || isFocused}
            submitted={submitted}
            onClick={() => {
              inputRef.current?.focus();
              setIsFocused(true);
            }}
            onMouseDown={(e) => {
              // Ensure the input takes focus BEFORE the wrapper can
              e.preventDefault();
              e.stopPropagation();
              inputRef.current?.focus();
              setIsFocused(true);
            }}
          >
            {showSuccessMessage ? label : ''}
          </FloatLabel>
          <MobilePhoneNumberInput
            {...inputProps}
            ref={inputRef}
            onFocus={() => {
              setIsFocused(true);
            }}
            onBlur={() => {
              setIsFocused(false);
            }}
            id={id ?? inputId}
            type="email"
            placeholder=""
            autoComplete={'email'}
            borderColor={borderColor}
            floatLabel={!!value || isFocused}
            submitted={submitted}
            responsive={responsive}
            value={submitted ? successMessage : value}
            data-e2e="email"
            data-testid="email"
            isReturning={isReturning}
            aria-invalid={!!errorMessage}
            aria-describedby={errorMessage ? errorId : undefined}
          />
          {showSuccessMessage && (
            <MobilePhoneNumberButton
              type="submit"
              cfButtonColor={buttonColor as CFColorTokenProp}
              cfButtonHoverColor={buttonHoverColor as CFColorTokenProp}
              data-e2e="btn-fail-whale"
              data-testid="btn-fail-whale"
            >
              {buttonText}
            </MobilePhoneNumberButton>
          )}
        </div>

        {showErrorBanner && errorMessage && (
          <div
            ref={errorRef}
            id={errorId}
            role="alert"
            aria-live="polite"
            tabIndex={-1}
            className={[
              'bg-[#035c67]',
              'w-full',
              'min-h-[25px]',
              'text-white',
              'text-center',
              'absolute',
              errorPosition === 'top'
                ? 'bottom-[55px] rounded-t-[20px] p-4'
                : 'top-[55px] rounded-b-[20px] p-4'
            ].join(' ')}
          >
            {errorMessage}
          </div>
        )}
      </div>
    </>
  );
}
